import { useState } from 'react';
import type { CustomerRecord } from '@/domain/types';
import { customerName } from '@/utils/format';
import CustomerFlagChips from '@/components/CustomerFlagChips';
import InstagramLink from '@/components/InstagramLink';
import LinkDmLeadPanel from '@/components/LinkDmLeadPanel';
import EditCustomerDetailsModal from '@/components/EditCustomerDetailsModal';
import { Pencil } from 'lucide-react';
import clsx from 'clsx';

export interface CustomerRecordHeaderProps {
  customer: CustomerRecord;
  /** tv-pzvy: show "Edit details" (a real customer record; a promoted tail record has no row to edit). */
  editable?: boolean;
}

export default function CustomerRecordHeader({ customer, editable = false }: CustomerRecordHeaderProps) {
  const score = customer.leadScore;
  const isDmLead = customer.source.channel === 'ig-dm' && customer.source.via === 'hookchat';
  const [linkPanelOpen, setLinkPanelOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const chipClass = clsx(
    'inline-flex items-center px-3 py-1 rounded-full text-xs uppercase tracking-widest font-inter',
    score >= 80
      ? 'bg-gold-bright text-ink-black'
      : score >= 50
        ? 'border border-gold-bright text-gold-bright'
        : 'border border-cream-soft text-cream-soft',
  );
  return (
    <header
      data-testid={`customer-header-${customer.id}`}
      className="bg-ink-dark border-b border-gold-dim/20 px-8 py-6 flex items-start justify-between gap-4"
    >
      <div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <h1
            className="font-inter text-[40px] leading-tight text-gold-bright"
            data-testid="customer-record-name"
          >
            {customerName(customer)}
          </h1>
          {editable ? (
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="btn-ghost text-xs inline-flex items-center gap-1.5 min-h-[44px]"
              aria-label="Edit customer details"
              data-testid="customer-edit-details"
            >
              <Pencil size={14} aria-hidden="true" /> Edit details
            </button>
          ) : null}
        </div>
        {isDmLead ? (
          <p className="mt-1 flex items-center gap-2">
            <span className="pill-cream text-[10px] uppercase tracking-widest">
              New from Instagram
            </span>
            <span className="font-inter text-[11px] italic text-cream-soft">
              Name as shown on Instagram, not verified
            </span>
          </p>
        ) : null}
        <p className="font-mono text-sm text-cream-soft mt-2 flex flex-wrap gap-x-6 gap-y-1">
          <span data-testid="customer-record-phone">{customer.phone}</span>
          {customer.email ? <span>{customer.email}</span> : null}
          <InstagramLink handle={customer.instagramHandle} />
        </p>
        <p className="font-inter text-xs text-cream-soft mt-2 uppercase tracking-widest">
          {customer.suburb}, {customer.state} {customer.postcode} ·{' '}
          {customer.distanceToNearestStudioKm}km to {customer.nearestStudioId.replace('-', ' ')}
        </p>
        {isDmLead ? (
          <div className="relative mt-3 inline-block">
            <button
              type="button"
              onClick={() => setLinkPanelOpen((o) => !o)}
              className="btn-quiet text-[13px]"
            >
              Link to existing customer
            </button>
            {linkPanelOpen ? (
              <LinkDmLeadPanel lead={customer} onClose={() => setLinkPanelOpen(false)} />
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="flex flex-col items-end gap-2">
        <span className={chipClass}>Lead score {score}</span>
        <span className="pill-cream text-[10px]">{customer.leadStage}</span>
        <span className="pill-cream text-[10px]">{customer.engagementStatus}</span>
        <CustomerFlagChips customer={customer} className="justify-end max-w-[240px]" />
      </div>
      {editable ? (
        <EditCustomerDetailsModal open={editOpen} onClose={() => setEditOpen(false)} customer={customer} />
      ) : null}
    </header>
  );
}
